import { isIconOnly } from "@/utils/IsIconOnly";
import { useClassNames } from "@/utils/classNames";

import styles from "./CoreBadge.module.scss";
import { CoreBadgeProps, CoreBadgeVariant } from "./types";

/**
 * CoreBadge component is used to display badges with different styles and sizes.
 *
 * @param variant - The visual style of the badge
 * @param size - The size of the badge
 * @param className - Additional CSS class for styling
 * @param children - Badge content, can include text or icons
 * @param attr - Additional HTML attributes to apply to the component
 *
 * @returns the CoreBadge component
 *
 * @example
 * ```tsx
 * <CoreBadge variant={CoreBadgeVariant.Black} size={CoreBadgeSize.Size24}>
 *   <Icon icon={StreamingIcons.OutOfMarket} />
 *   Out Of Market
 * </CoreBadge>
 * ```
 */
const CoreBadge = ({
  variant = CoreBadgeVariant.White,
  size,
  className,
  children,
  ...attr
}: CoreBadgeProps) => {
  /* If there is no text, add icon only styling */
  const iconOnly = isIconOnly(children);

  const cn = useClassNames(
    styles,
    styles["core-badge"],
    styles[variant],
    styles[`size-${size}`],
    iconOnly && styles["icon-only"],
    className,
  );

  return (
    <div className={cn} {...attr}>
      {children}
    </div>
  );
};

export { CoreBadge };
